import React from 'react';
import { BrowserRouter, Router } from 'react-router-dom';
import {
  CoreAuth,
  RemoteLifecyleEventArgs,
  mountReactComponent,
  RemoteApplicationAdapter,
  unmountReactComponent,
} from '@amzn/dots-core-ui';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AppRouter, AppNavigation, App } from './features';
import { ClientProvider } from './api';
import { environment } from './environments';

// //Uncomment the lines below to run mocks locally
import mockServer from './mock/mockServer';

if (process.env.NODE_ENV === 'development') {
  mockServer();
}

declare global {
  interface Window {
    DotsStorage: RemoteApplicationAdapter;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // do not trigger a refetch when the window re-focuses
      refetchOnWindowFocus: false,
      // don't retry failed requests, it probably won't help
      retry: 0,
    },
  },
});

window.DotsStorage = {
  mount: ({ containerId }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <CoreAuth userManagerSettings={environment.userManagerSettings}>
            <ClientProvider storageApiUrl={environment.storageApiUrl}>
              <App />
            </ClientProvider>
          </CoreAuth>
        </BrowserRouter>
      </QueryClientProvider>,
      containerId
    ),
  mountRouter: ({
    containerId,
    history,
    createToken,
  }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <CoreAuth userManagerSettings={environment.userManagerSettings}>
            <ClientProvider
              storageApiUrl={environment.storageApiUrl}
              createToken={createToken}
            >
              <AppRouter isRootApp={false} />
            </ClientProvider>
          </CoreAuth>
        </Router>
      </QueryClientProvider>,
      containerId
    ),
  mountNavigation: ({
    containerId,
    history,
    isMenuOpen,
  }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <CoreAuth userManagerSettings={environment.userManagerSettings}>
            <AppNavigation isMenuOpen={isMenuOpen} />
          </CoreAuth>
        </Router>
      </QueryClientProvider>,
      containerId
    ),
  unmountRouter: unmountReactComponent,
  unmountNavigation: unmountReactComponent,
};
